import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  Package, 
  Tag, 
  DollarSign, 
  Building, 
  Grid3x3, 
  ArrowUp, 
  ArrowDown,
  ExternalLink,
  RefreshCw,
  FileText,
  Globe,
  List
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import DonutChartWithTable, { DataItem } from './DonutChartWithTable';
import ImageModal from './ImageModal';
import InstallmentsChart from './InstallmentsChart';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';
import FilteredProductsModal from './FilteredProductsModal';
import { generateStatisticsPDF } from '../utils/pdfGenerator';
import api from '../utils/api';
import { debugStep } from '../utils/debugStep';
import { useClient } from '../contexts/ClientContext';
import { SkeletonStatCard, SkeletonChart } from './skeletons';

interface SummaryData {
  totalPublicaciones: number;
  publicacionesConDescuento: number;
  porcentajeDescuento: number;
  descuentoPromedio: number;
  precioMediano: number;
  precioMinimo: {
    valor: number;
    producto: any;
  };
  precioMaximo: {
    valor: number;
    producto: any;
  };
  cantidadMarcas: number;
  cantidadCategorias: number;
  cantidadSitios: number;
}

interface BrandData {
  shareMarcias: {
    data: Array<{
      marca: string;
      cantidad: number;
      porcentaje: number;
    }>;
    totalPublicaciones: number;
  };
}

interface CategoryData {
  shareCategorias: {
    data: Array<{
      categoria: string;
      cantidad: number;
      porcentaje: number;
    }>;
    totalPublicaciones: number;
  };
}

interface SiteData {
  publicacionesPorSitio: {
    data: Array<{
      sitio: string;
      cantidad: number;
      porcentaje: number;
    }>;
    totalPublicaciones: number;
  };
}

interface PriceRangeData {
  rangosPreciosData: {
    data: Array<{
      rango: string;
      cantidad: number;
      porcentaje: number;
      desde: number;
      hasta: number;
    }>;
    totalPublicaciones: number;
  };
}

interface InstallmentsData {
  installmentsDistribution: {
    data: Array<{
      installments: number | null;
      cantidad: number;
      porcentaje: number;
    }>;
    totalPublicaciones: number;
    hasInstallmentsColumn?: boolean;
  };
}

interface PriceRangeChartProps {
  data: Array<{
    rango: string;
    cantidad: number;
    porcentaje: number;
    desde: number;
    hasta: number;
  }>;
  title: string;
  subtitle?: string;
  t: (key: string, options?: any) => string;
}

const PriceRangeChart: React.FC<PriceRangeChartProps> = ({ data, title, subtitle, t }) => {
  const getBarColor = (index: number, total: number) => {
    const colors = [
      '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B',
      '#EF4444', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
      '#EC4899', '#14B8A6'
    ];

    if (total <= colors.length) {
      return colors[index] || colors[colors.length - 1];
    }

    const colorIndex = Math.floor((index / total) * (colors.length - 1));
    return colors[colorIndex] || colors[colors.length - 1];
  };

  const formatNumber = (num: number): string => new Intl.NumberFormat('es-ES').format(num);
  const formatPercentage = (num: number): string => `${num.toFixed(1)}%`;

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const tooltipData = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800">{`${t('statistics.common_labels.range')}: ${label}`}</p>
          <p className="text-blue-600">{`${t('statistics.common_labels.quantity')}: ${formatNumber(tooltipData.cantidad)} ${t('statistics.common_labels.publications')}`}</p>
          <p className="text-gray-600">{`${t('statistics.common_labels.percentage')}: ${formatPercentage(tooltipData.porcentaje)}`}</p>
        </div>
      );
    }
    return null;
  };

  const renderCustomLabel = (props: any) => {
    const { x, y, width, height, payload, value } = props;
    if (height < 25) return null;

    const labelY = y + height / 2;
    const labelX = x + width / 2;
    const cantidad = payload?.cantidad ?? value ?? 0;
    const porcentaje = payload?.porcentaje;

    let labelText = formatNumber(cantidad);
    if (porcentaje !== undefined) {
      labelText = `${formatNumber(cantidad)} (${formatPercentage(porcentaje)})`;
    }

    return (
      <text
        x={labelX}
        y={labelY}
        fill="#ffffff"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize="11"
        fontWeight="bold"
        style={{
          textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
          filter: 'drop-shadow(1px 1px 1px rgba(0,0,0,0.5))'
        }}
      >
        {labelText}
      </text>
    );
  };

  if (!data || data.length === 0) {
    return null;
  }

  const filteredData = data.filter(item => item.cantidad > 0);

  if (filteredData.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">{title}</h3>
        {subtitle && <p className="text-gray-600">{subtitle}</p>}
      </div>

      <div className="h-80 mb-1">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={filteredData}
            margin={{ top: 15, right: 30, left: 20, bottom: 42 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="rango"
              interval={0}
              tick={(props: any) => {
                const { x, y, payload } = props;
                const parts = (payload?.value || '').split(' - ');
                if (parts.length === 2) {
                  return (
                    <g transform={`translate(${x},${y})`}>
                      <text x={0} y={0} dy={16} textAnchor="middle" fontSize={9} fill="#374151" fontWeight="500">
                        {parts[0]}
                      </text>
                      <text x={0} y={0} dy={28} textAnchor="middle" fontSize={8} fill="#9CA3AF">
                        —
                      </text>
                      <text x={0} y={0} dy={40} textAnchor="middle" fontSize={9} fill="#374151" fontWeight="500">
                        {parts[1]}
                      </text>
                    </g>
                  );
                }
                return (
                  <text x={x} y={y} dy={16} textAnchor="middle" fontSize={9} fill="#374151">
                    {payload?.value}
                  </text>
                );
              }}
              height={100}
            />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={formatNumber} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="cantidad" radius={[4, 4, 0, 0]}>
              <LabelList content={renderCustomLabel} />
              {filteredData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getBarColor(index, filteredData.length)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="overflow-x-auto max-w-4xl mx-auto">
        <table className="w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('statistics.table_headers.price_range')}
              </th>
              <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('statistics.table_headers.quantity')}
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('statistics.table_headers.percentage')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredData.map((item, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-3 py-2 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="w-3 h-3 rounded mr-2" style={{ backgroundColor: getBarColor(index, filteredData.length) }} />
                    <div className="text-sm font-medium text-gray-900">{item.rango}</div>
                  </div>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-center">
                  <div className="text-sm text-gray-900 font-medium">{formatNumber(item.cantidad)}</div>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-right">
                  <div className="text-sm text-gray-900 font-medium">{formatPercentage(item.porcentaje)}</div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1 max-w-[100px] ml-auto">
                    <div
                      className="h-1.5 rounded-full"
                      style={{ width: `${Math.min(item.porcentaje, 100)}%`, backgroundColor: getBarColor(index, data.length) }}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

interface StatisticsPanelProps {
  tableName: string;
  filters?: any;
  tableDate?: string;
  activeFilterName?: string | null;
}

const StatisticsPanel: React.FC<StatisticsPanelProps> = ({ tableName, filters, tableDate, activeFilterName }) => {
  const { t } = useTranslation();
  const { selectedClient } = useClient();
  
  // Individual state for each section
  const [summaryData, setSummaryData] = useState<SummaryData | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  
  const [brandData, setBrandData] = useState<BrandData | null>(null);
  const [brandLoading, setBrandLoading] = useState(true);
  const [brandError, setBrandError] = useState<string | null>(null);
  
  const [categoryData, setCategoryData] = useState<CategoryData | null>(null);
  const [categoryLoading, setCategoryLoading] = useState(true);
  const [categoryError, setCategoryError] = useState<string | null>(null);
  
  const [siteData, setSiteData] = useState<SiteData | null>(null);
  const [siteLoading, setSiteLoading] = useState(true);
  const [siteError, setSiteError] = useState<string | null>(null);
  
  const [priceRangeData, setPriceRangeData] = useState<PriceRangeData | null>(null);
  const [priceRangeLoading, setPriceRangeLoading] = useState(true);
  const [priceRangeError, setPriceRangeError] = useState<string | null>(null);
  
  const [installmentsData, setInstallmentsData] = useState<InstallmentsData | null>(null);
  const [installmentsLoading, setInstallmentsLoading] = useState(true);
  const [installmentsError, setInstallmentsError] = useState<string | null>(null);
  const installmentsDistribution = installmentsData?.installmentsDistribution;
  const hasInstallmentsColumn = installmentsDistribution?.hasInstallmentsColumn !== false;
  const hasInstallmentsRecords = (installmentsDistribution?.data || []).some(item => item.cantidad > 0);
  
  const [exporting, setExporting] = useState(false);
  
  // Estados para el modal de imagen
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{
    url: string;
    title: string;
  } | null>(null);

  // Estados para el modal de productos filtrados (estadísticas generales)
  const [filteredModalOpen, setFilteredModalOpen] = useState(false);
  const [filteredModalConfig, setFilteredModalConfig] = useState<{
    filterType?: 'brand' | 'category' | 'site' | 'discount' | 'all';
    filterValue?: string;
    title: string;
    filters?: any;
  } | null>(null);

  // Funciones para manejar el modal de imagen
  const openImageModal = (imageUrl: string, title: string) => {
    setSelectedImage({
      url: imageUrl,
      title: title
    });
    setImageModalOpen(true);
  };

  const closeImageModal = () => {
    setImageModalOpen(false);
    setSelectedImage(null);
  };

  // Funciones para manejar el modal de productos filtrados
  const openFilteredModal = (config: {
    filterType?: 'brand' | 'category' | 'site' | 'discount' | 'all';
    filterValue?: string;
    title: string;
    filters?: any;
  }) => {
    setFilteredModalConfig(config);
    setFilteredModalOpen(true);
  };

  const closeFilteredModal = () => {
    setFilteredModalOpen(false);
    setFilteredModalConfig(null);
  };

  // Handler para clicks en items de estadísticas generales
  const handleGeneralStatItemClick = (item: DataItem, dataKey: 'marca' | 'categoria' | 'sitio') => {
    const filterLabel = dataKey === 'marca' ? t('statistics.table_headers.brand') :
                       dataKey === 'categoria' ? t('statistics.table_headers.category') :
                       t('statistics.table_headers.site');
    
    openFilteredModal({
      filterType: dataKey === 'marca' ? 'brand' : dataKey === 'categoria' ? 'category' : 'site',
      filterValue: item[dataKey] || '',
      title: `${filterLabel}: ${item[dataKey] || ''}`,
      filters: filters
    });
  };

  // Función para convertir texto a title case (cada palabra con primera letra mayúscula)
  const toTitleCase = (str: string): string => {
    if (!str) return str;
    
    return str
      .toLowerCase()
      .split(' ')
      .map(word => {
        // Mantener ciertas palabras en minúsculas (artículos, preposiciones, etc.)
        const lowercaseWords = ['de', 'del', 'la', 'el', 'las', 'los', 'y', 'o', 'para', 'con', 'en', 'sin', 'por'];
        if (lowercaseWords.includes(word) && word.length > 0) {
          return word;
        }
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ')
      // Capitalizar siempre la primera palabra
      .replace(/^[a-z]/, match => match.toUpperCase());
  };

  useEffect(() => {
    if (tableName) {
      loadSummaryStatistics();
      loadBrandStatistics();
      loadCategoryStatistics();
      loadSiteStatistics();
      loadPriceRangeStatistics();
      loadInstallmentsStatistics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableName, filters]);

  const buildQueryParams = () => {
    const params = new URLSearchParams();
    if (filters && typeof filters === 'object' && Object.keys(filters).length > 0) {
      Object.entries(filters).forEach(([key, value]) => {
        // Ignorar in_out_filter legacy (feature In/Out eliminada, PT-#95)
        if (key !== 'in_out_filter' && value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
    }
    return params;
  };

  const loadSummaryStatistics = async () => {
    setSummaryLoading(true);
    setSummaryError(null);
    const finish = debugStep('stats:summary', { tableName });
    try {
      const params = buildQueryParams();
      const url = `/api/tables/${tableName}/statistics/summary${params.toString() ? '?' + params.toString() : ''}`;
      const response = await api.get(url);
      setSummaryData(response.data);
    } catch (err) {
      console.error('❌ Error loading summary statistics:', err);
      setSummaryError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      try { finish(); } catch { /* noop */ }
      setSummaryLoading(false);
    }
  };

  const loadBrandStatistics = async () => {
    setBrandLoading(true);
    setBrandError(null);
    const finish = debugStep('stats:brands', { tableName });
    try {
      const params = buildQueryParams();
      const url = `/api/tables/${tableName}/statistics/brands${params.toString() ? '?' + params.toString() : ''}`;
      const response = await api.get(url);
      setBrandData(response.data);
    } catch (err) {
      console.error('❌ Error loading brand statistics:', err);
      setBrandError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      try { finish(); } catch { /* noop */ }
      setBrandLoading(false);
    }
  };

  const loadCategoryStatistics = async () => {
    setCategoryLoading(true);
    setCategoryError(null);
    const finish = debugStep('stats:categories', { tableName });
    try {
      const params = buildQueryParams();
      const url = `/api/tables/${tableName}/statistics/categories${params.toString() ? '?' + params.toString() : ''}`;
      const response = await api.get(url);
      setCategoryData(response.data);
    } catch (err) {
      console.error('❌ Error loading category statistics:', err);
      setCategoryError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      try { finish(); } catch { /* noop */ }
      setCategoryLoading(false);
    }
  };

  const loadSiteStatistics = async () => {
    setSiteLoading(true);
    setSiteError(null);
    const finish = debugStep('stats:sites', { tableName });
    try {
      const params = buildQueryParams();
      const url = `/api/tables/${tableName}/statistics/sites${params.toString() ? '?' + params.toString() : ''}`;
      const response = await api.get(url);
      setSiteData(response.data);
    } catch (err) {
      console.error('❌ Error loading site statistics:', err);
      setSiteError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      try { finish(); } catch { /* noop */ }
      setSiteLoading(false);
    }
  };

  const loadPriceRangeStatistics = async () => {
    setPriceRangeLoading(true);
    setPriceRangeError(null);
    const finish = debugStep('stats:price-ranges', { tableName });
    try {
      const params = buildQueryParams();
      const url = `/api/tables/${tableName}/statistics/price-ranges${params.toString() ? '?' + params.toString() : ''}`;
      const response = await api.get(url);
      setPriceRangeData(response.data);
    } catch (err) {
      console.error('❌ Error loading price range statistics:', err);
      setPriceRangeError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      try { finish(); } catch { /* noop */ }
      setPriceRangeLoading(false);
    }
  };

  const loadInstallmentsStatistics = async () => {
    setInstallmentsLoading(true);
    setInstallmentsError(null);
    const finish = debugStep('stats:installments', { tableName });
    try {
      const params = buildQueryParams();
      const url = `/api/tables/${tableName}/statistics/installments${params.toString() ? '?' + params.toString() : ''}`;
      const response = await api.get(url);
      setInstallmentsData(response.data);
    } catch (err) {
      console.error('❌ Error loading installments statistics:', err);
      setInstallmentsError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      try { finish(); } catch { /* noop */ }
      setInstallmentsLoading(false);
    }
  };

  const refreshAll = () => {
    loadSummaryStatistics();
    loadBrandStatistics();
    loadCategoryStatistics();
    loadSiteStatistics();
    loadPriceRangeStatistics();
    loadInstallmentsStatistics();
  };

  const handleExportPDF = async () => {
    
    // Validar que estén cargados todos los datos de estadísticas generales
    if (!summaryData || !brandData || !categoryData || !siteData || !priceRangeData || !installmentsData || !tableDate) {
      console.error('Missing required data for general statistics PDF export');
      alert('Esperando a que se carguen todos los datos de estadísticas generales...');
      return;
    }

    setExporting(true);
    try {
      // Combine general statistics data for PDF export
      const combinedStatistics = {
        ...summaryData,
        ...brandData,
        ...categoryData,
        ...siteData,
        ...priceRangeData,
        ...installmentsData
      };
      
      await generateStatisticsPDF({
        tableName,
        tableDate: tableDate || '',
        filters: filters || {},
        statistics: combinedStatistics as any,
        t,
        activeFilterName,
        clientName: selectedClient?.clientName
      });
    } catch (error) {
      console.error('Error exporting PDF:', error);
      console.error('Error details:', error instanceof Error ? error.stack : error);
      alert(`Error al exportar PDF: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount: number | string | null | undefined): string => {
    const numAmount = parseFloat(String(amount || 0));
    if (isNaN(numAmount)) return '$0';
    
    // Determinar si tiene centavos
    const hasCents = numAmount % 1 !== 0;
    
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: hasCents ? 2 : 0,
    }).format(numAmount);
  };

  const formatNumber = (num: number | string | null | undefined): string => {
    const numValue = parseInt(String(num || 0));
    if (isNaN(numValue)) return '0';
    
    return new Intl.NumberFormat('es-ES').format(numValue);
  };

  const formatPercentage = (num: number | string | null | undefined): string => {
    const numValue = parseFloat(String(num || 0));
    if (isNaN(numValue)) return '0.0%';
    
    return `${numValue.toFixed(1)}%`;
  };

  const StatisticCard = ({ 
    icon: Icon, 
    title, 
    value, 
    subtitle, 
    color = 'blue',
    onClick 
  }: {
    icon: any;
    title: string;
    value: string | number;
    subtitle?: string;
    color?: 'blue' | 'green' | 'orange' | 'purple' | 'red' | 'yellow';
    onClick?: () => void;
  }) => {
    const colorClasses = {
      blue: 'bg-blue-50 border-blue-200 text-blue-800',
      green: 'bg-green-50 border-green-200 text-green-800',
      orange: 'bg-orange-50 border-orange-200 text-orange-800',
      purple: 'bg-purple-50 border-purple-200 text-purple-800',
      red: 'bg-red-50 border-red-200 text-red-800',
      yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800'
    };

    const iconColorClasses = {
      blue: 'text-blue-500',
      green: 'text-green-500',
      orange: 'text-orange-500',
      purple: 'text-purple-500',
      red: 'text-red-500',
      yellow: 'text-yellow-500'
    };

    return (
      <div 
        className={`p-6 rounded-lg border ${colorClasses[color]} ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow group' : ''} relative`}
        onClick={onClick}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
            {subtitle && (
              <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
            )}
          </div>
          <Icon className={`h-8 w-8 ${iconColorClasses[color]}`} />
        </div>
        {onClick && (
          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <List className="h-4 w-4 text-gray-600" />
          </div>
        )}
      </div>
    );
  };

  const ProductCard = ({ producto, tipo }: { producto: any; tipo: 'min' | 'max' }) => {
    if (!producto) return null;

    // Obtener la URL del producto
    const productUrl = producto.url || producto.link || producto.href;
    
    // Extraer el sitio de la URL si no está disponible directamente
    const getSitioFromUrl = (url: string) => {
      try {
        return new URL(url).hostname.replace('www.', '');
      } catch {
        return t('statistics.common_labels.unknown_site');
      }
    };
    
    const sitio = producto.sitio || producto.site || (productUrl ? getSitioFromUrl(productUrl) : t('statistics.common_labels.unknown_site'));

    const handleOpenProduct = () => {
      if (productUrl) {
        window.open(productUrl, '_blank', 'noopener,noreferrer');
      }
    };

    return (
      <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-start space-x-3">
          {producto.imagen && (
            <div 
              className="relative group cursor-pointer flex-shrink-0"
              onClick={() => openImageModal(
                producto.imagen, 
                toTitleCase(producto.titulo || producto.title || t('statistics.common_labels.no_title'))
              )}
            >
              <img 
                src={producto.imagen} 
                alt={toTitleCase(producto.titulo || producto.title || t('statistics.common_labels.no_title'))}
                className="w-16 h-16 object-cover rounded-lg border border-gray-200 hover:border-blue-400 transition-all duration-200 group-hover:shadow-lg group-hover:scale-105"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
              {/* Overlay de hover */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 rounded-lg transition-all duration-200 flex items-center justify-center">
                <div className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                  </svg>
                </div>
              </div>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-gray-900 truncate">
                  {toTitleCase(producto.titulo || producto.title || t('statistics.common_labels.no_title'))}
                </h4>
                <p className="text-sm text-gray-500 truncate">
                  {(producto.marca || producto.Marca || producto.brand || t('statistics.common_labels.no_brand'))} - {(producto.categoria || producto.Cat || producto.category || t('statistics.common_labels.no_category'))}
                </p>
              </div>
              
              {/* Botón para abrir en sitio original */}
              {productUrl && (
                <button
                  onClick={handleOpenProduct}
                  className="ml-2 p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-full transition-colors flex-shrink-0"
                  title={t('statistics.common_labels.open_in_original_site')}
                >
                  <ExternalLink className="h-4 w-4" />
                </button>
              )}
            </div>
            
            {/* Sitio web */}
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-gray-500">
                📍 {sitio}
              </span>
              
              {/* Badge del tipo de precio */}
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                tipo === 'min' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-red-100 text-red-700'
              }`}>
                {tipo === 'min' ? t('statistics.cheapest') : t('statistics.most_expensive')}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Loading spinner component for sections
  const SectionLoader = () => (
    <div className="flex items-center justify-center py-8">
      <RefreshCw className="h-6 w-6 text-blue-500 animate-spin mr-2" />
      <span className="text-gray-600">{t('statistics.loading')}</span>
    </div>
  );

  // Error component for sections
  const SectionError = ({ error, onRetry }: { error: string; onRetry: () => void }) => (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
      <p className="text-red-800">{error}</p>
      <button
        onClick={onRetry}
        className="mt-2 text-sm text-red-600 hover:text-red-700 underline"
      >
        {t('statistics.retry')}
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header con título y botones */}
      <div className="flex items-center justify-between py-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">{t('statistics.dashboard_title')}</h2>
          <p className="text-gray-600 mt-1">
            {t('statistics.dashboard_subtitle')}
            {activeFilterName && (
              <span className="ml-2 text-sm font-medium text-blue-600">
                ({activeFilterName})
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          {summaryData && summaryData.totalPublicaciones > 0 && (
            <button
              onClick={handleExportPDF}
              disabled={exporting || !tableDate}
              className="flex items-center gap-1 px-3 py-2 text-red-600 hover:text-red-700 border border-red-300 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={t('statistics.export_pdf')}
            >
              <FileText className="h-4 w-4" />
              <span className="text-sm font-medium">PDF</span>
            </button>
          )}
          <button
            onClick={refreshAll}
            className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('statistics.refresh')}
          </button>
        </div>
      </div>

      {/* Tabs de selección de sección */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            <div className="py-2 px-6 border-b-2 font-medium text-sm border-blue-500 text-blue-600">
              {t('statistics.general_stats')}
            </div>
          </nav>
        </div>
      </div>

      {/* Contenido: estadísticas generales */}
        <>
          {/* Summary Statistics Section */}
          {summaryLoading ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[1,2,3,4].map(i => <SkeletonStatCard key={i} />)}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[1,2,3].map(i => <SkeletonStatCard key={i} />)}
              </div>
            </div>
          ) : summaryError ? (
            <SectionError error={summaryError} onRetry={loadSummaryStatistics} />
          ) : summaryData && summaryData.totalPublicaciones === 0 ? (
            /* Mensaje único cuando no hay resultados */
            <div className="my-12">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-12 text-center">
                <div className="max-w-lg mx-auto">
                  <h3 className="text-xl font-semibold text-amber-900 mb-3">
                    {t('statistics.no_results_title', 'No se encontraron resultados')}
                  </h3>
                  <p className="text-amber-700 text-base">
                    {t('statistics.no_results_description', 'Los filtros aplicados no han devuelto ningún producto. Intenta modificar los criterios de búsqueda.')}
                  </p>
                </div>
              </div>
            </div>
          ) : summaryData ? (
            <div className="fade-in space-y-6">
          {/* Grid de estadísticas principales */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 statistics-overview-cards">
            {/* Cantidad de publicaciones */}
            <StatisticCard
              icon={Package}
              title={t('statistics.total_publications')}
              value={formatNumber(summaryData.totalPublicaciones)}
              color="blue"
              onClick={() => openFilteredModal({
                title: t('statistics.total_publications'),
                filters: filters
              })}
            />

            {/* Publicaciones con descuento */}
            <StatisticCard
              icon={Tag}
              title={t('statistics.publications_with_discount')}
              value={`${formatNumber(summaryData.publicacionesConDescuento || 0)} (${formatPercentage(summaryData.porcentajeDescuento || 0)})`}
              color="green"
              onClick={() => openFilteredModal({
                filterType: 'discount',
                filterValue: 'with',
                title: t('statistics.publications_with_discount'),
                filters: filters
              })}
            />

            {/* Descuento promedio */}
            <StatisticCard
              icon={TrendingUp}
              title={t('statistics.average_discount')}
              value={formatPercentage(summaryData.descuentoPromedio || 0)}
              color="orange"
            />

            {/* Precio mediano */}
            <StatisticCard
              icon={DollarSign}
              title={t('statistics.median_price')}
              value={formatCurrency(summaryData.precioMediano || 0)}
              color="purple"
            />
          </div>

          {/* Grid de sitios, marcas y categorías */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <StatisticCard
              icon={Globe}
              title={t('statistics.total_sites')}
              value={formatNumber(summaryData.cantidadSitios || 0)}
              subtitle={t('statistics.sites_subtitle')}
              color="blue"
            />

            <StatisticCard
              icon={Building}
              title={t('statistics.total_brands')}
              value={formatNumber(summaryData.cantidadMarcas || 0)}
              subtitle={t('statistics.brands_subtitle')}
              color="yellow"
            />

            <StatisticCard
              icon={Grid3x3}
              title={t('statistics.total_categories')}
              value={formatNumber(summaryData.cantidadCategorias || 0)}
              subtitle={t('statistics.categories_subtitle')}
              color="purple"
            />
          </div>
        </div>
      ) : null}

      {/* Gráficos de dona con tablas - Solo mostrar si hay datos */}
      {summaryData && summaryData.totalPublicaciones > 0 && (
        <div className="space-y-6">
          {/* Share de Marcas */}
          <div className="brand-share-chart">
          {brandLoading ? (
            <SkeletonChart />
          ) : brandError ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('statistics.brand_share')}</h3>
              <SectionError error={brandError} onRetry={loadBrandStatistics} />
            </div>
          ) : brandData?.shareMarcias?.data && Array.isArray(brandData.shareMarcias.data) && brandData.shareMarcias.data.length > 0 ? (
            <DonutChartWithTable
              data={brandData.shareMarcias.data}
              totalPublicaciones={brandData.shareMarcias.totalPublicaciones}
              title={t('statistics.brand_share')}
              subtitle={t('statistics.brand_share_subtitle')}
              dataKey="marca"
              colors={[
                '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
                '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16'
              ]}
              tableName={tableName}
              isClickable={true}
              onItemClick={(item) => handleGeneralStatItemClick(item, 'marca')}
            />
          ) : null}
        </div>

        {/* Share de Categorías */}
        <div className="category-share-chart">
          {categoryLoading ? (
            <SkeletonChart />
          ) : categoryError ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('statistics.category_share')}</h3>
              <SectionError error={categoryError} onRetry={loadCategoryStatistics} />
            </div>
          ) : categoryData?.shareCategorias?.data && Array.isArray(categoryData.shareCategorias.data) && categoryData.shareCategorias.data.length > 0 ? (
            <DonutChartWithTable
              data={categoryData.shareCategorias.data}
              totalPublicaciones={categoryData.shareCategorias.totalPublicaciones}
              title={t('statistics.category_share')}
              subtitle={t('statistics.category_share_subtitle')}
              dataKey="categoria"
              colors={[
                '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#6366F1',
                '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#84CC16'
              ]}
              tableName={tableName}
              isClickable={true}
              onItemClick={(item) => handleGeneralStatItemClick(item, 'categoria')}
            />
          ) : null}
        </div>

        {/* Publicaciones por Sitio */}
        <div className="site-listings-chart">
          {siteLoading ? (
            <SkeletonChart />
          ) : siteError ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('statistics.publications_by_site')}</h3>
              <SectionError error={siteError} onRetry={loadSiteStatistics} />
            </div>
          ) : siteData?.publicacionesPorSitio?.data && Array.isArray(siteData.publicacionesPorSitio.data) && siteData.publicacionesPorSitio.data.length > 0 ? (
            <DonutChartWithTable
              data={siteData.publicacionesPorSitio.data}
              totalPublicaciones={siteData.publicacionesPorSitio.totalPublicaciones}
              title={t('statistics.publications_by_site')}
              subtitle={t('statistics.publications_by_site_subtitle')}
              dataKey="sitio"
              colors={[
                '#10B981', '#F59E0B', '#EF4444', '#3B82F6', '#8B5CF6',
                '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16'
              ]}
              tableName={tableName}
              isClickable={true}
              onItemClick={(item) => handleGeneralStatItemClick(item, 'sitio')}
            />
          ) : null}
        </div>
      </div>
      )}

      {/* Análisis de Rangos de Precios */}
      {summaryData && summaryData.totalPublicaciones > 0 && (
        <div className="price-range-section">
          {priceRangeLoading ? (
            <SkeletonChart />
          ) : priceRangeError ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('statistics.price_range_analysis')}</h3>
              <SectionError error={priceRangeError} onRetry={loadPriceRangeStatistics} />
            </div>
          ) : priceRangeData?.rangosPreciosData?.data && Array.isArray(priceRangeData.rangosPreciosData.data) && priceRangeData.rangosPreciosData.data.length > 0 ? (
            <PriceRangeChart
              data={priceRangeData.rangosPreciosData.data}
              title={t('statistics.price_range_analysis')}
              subtitle={t('statistics.price_range_subtitle')}
              t={t}
            />
          ) : null}
        </div>
      )}

      {/* Distribución por cantidad de cuotas */}
      {summaryData && summaryData.totalPublicaciones > 0 && (
        <div className="installments-distribution-section">
          {installmentsLoading ? (
            <SkeletonChart />
          ) : installmentsError ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('statistics.installments.distribution_title')}</h3>
              <SectionError error={installmentsError} onRetry={loadInstallmentsStatistics} />
            </div>
          ) : hasInstallmentsRecords ? (
            <InstallmentsChart
              data={installmentsDistribution?.data || []}
              title={t('statistics.installments.distribution_title')}
              subtitle={t('statistics.installments.distribution_subtitle')}
            />
          ) : null}
        </div>
      )}

      {/* Grid de precios extremos - Solo mostrar si hay datos */}
      {summaryData && summaryData.totalPublicaciones > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 price-extremes-section">
          {/* Precio mínimo */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-green-800">{t('statistics.minimum_price')}</h3>
                <p className="text-3xl font-bold text-green-600">
                  {formatCurrency(summaryData.precioMinimo?.valor || 0)}
                </p>
              </div>
              <ArrowDown className="h-8 w-8 text-green-500" />
            </div>
            <ProductCard producto={summaryData.precioMinimo?.producto} tipo="min" />
          </div>

          {/* Precio máximo */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-red-800">{t('statistics.maximum_price')}</h3>
                <p className="text-3xl font-bold text-red-600">
                  {formatCurrency(summaryData.precioMaximo?.valor || 0)}
                </p>
              </div>
              <ArrowUp className="h-8 w-8 text-red-500" />
            </div>
            <ProductCard producto={summaryData.precioMaximo?.producto} tipo="max" />
          </div>
        </div>
      )}
        </>
      
      {/* Modal de imagen */}
      <ImageModal
        isOpen={imageModalOpen}
        onClose={closeImageModal}
        imageUrl={selectedImage?.url || ''}
        productTitle={selectedImage?.title || ''}
      />

      {/* Modal de productos filtrados (estadísticas generales) */}
      {filteredModalOpen && filteredModalConfig && (
        <FilteredProductsModal
          isOpen={filteredModalOpen}
          onClose={closeFilteredModal}
          tableName={tableName}
          filterType={filteredModalConfig.filterType}
          filterValue={filteredModalConfig.filterValue}
          title={filteredModalConfig.title}
          filters={filteredModalConfig.filters}
        />
      )}
    </div>
  );
};

export default StatisticsPanel; 
